// Theme configuration for gmach-finance
// Supports three themes: "original" (current design), "loop" (new design from Loop),
// and "loop-full" (full Loop layout with persistent sidebar)

export type ThemeName = 'original' | 'loop' | 'loop-full';

/**
 * Layout mode controls the shell structure of the app:
 * - 'header': classic top-header layout (original + loop themes)
 * - 'sidebar': persistent right-side sidebar nav (loop-full theme only)
 */
export type LayoutMode = 'header' | 'sidebar';

export interface ThemeColors {
  // Primary colors
  primary: string;
  primaryDark: string;
  primaryLight: string;
  primaryForeground: string;
  
  // Semantic colors
  success: string;
  warning: string;
  danger: string;
  info: string;
  
  // Surface colors
  surface: string;
  surfaceAlt: string;
  surfacePage: string;
  
  // Border colors
  border: string;
  borderSubtle: string;
  
  // Text colors
  text: string;
  textMuted: string;
  textHeading: string;
}

export interface ThemeConfig {
  name: string;
  displayName: string;
  description: string;
  fontFamily: string;
  headingFontFamily: string;
  colors: ThemeColors;
  /** Shell layout for this theme. Defaults to 'header' if omitted. */
  layoutMode?: LayoutMode;
}

export const themes: Record<ThemeName, ThemeConfig> = {
  original: {
    name: 'original',
    displayName: 'מקורי',
    description: 'העיצוב המקורי עם פונט Rubik וצבעים כחולים',
    fontFamily: "'Rubik', system-ui, -apple-system, sans-serif",
    headingFontFamily: "'Varela Round', 'Rubik', sans-serif",
    colors: {
      // Primary - Blue
      primary: '#1e40af',
      primaryDark: '#1e3a8a',
      primaryLight: '#3b82f6',
      primaryForeground: '#ffffff',
      
      // Semantic
      success: '#10b981',
      warning: '#f59e0b',
      danger: '#ef4444',
      info: '#3b82f6',
      
      // Surface
      surface: '#ffffff',
      surfaceAlt: '#f8fafc',
      surfacePage: '#f8fafc',
      
      // Border
      border: '#e2e8f0',
      borderSubtle: '#cbd5e1',
      
      // Text
      text: '#1e293b',
      textMuted: '#64748b',
      textHeading: '#0f172a',
    },
  },
  
  loop: {
    name: 'loop',
    displayName: 'Loop',
    description: 'עיצוב חדש עם פונט Noto Sans Hebrew וצבעים כחולים',
    fontFamily: "'Noto Sans Hebrew', sans-serif",
    headingFontFamily: "'Noto Sans Hebrew', sans-serif",
    layoutMode: 'header',
    colors: {
      // Primary - Deep Blue
      primary: '#18317a',
      primaryDark: '#0d1a42',
      primaryLight: '#264cbd',
      primaryForeground: '#ffffff',
      
      // Semantic
      success: '#10b981',
      warning: '#f59e0b',
      danger: '#f3463d',
      info: '#264cbd',
      
      // Surface
      surface: '#ffffff',
      surfaceAlt: '#f5f7fc',
      surfacePage: '#f8f9fd',
      
      // Border
      border: '#dde1ed',
      borderSubtle: '#c3cade',
      
      // Text
      text: '#272b38',
      textMuted: '#757f9a',
      textHeading: '#272b38',
    },
  },

  'loop-full': {
    name: 'loop-full',
    displayName: 'Loop מלא',
    description: 'אותו עיצוב של Loop, אבל עם תפריט צד קבוע (sidebar) בכל המערכת ובלי header עליון',
    fontFamily: "'Noto Sans Hebrew', sans-serif",
    headingFontFamily: "'Noto Sans Hebrew', sans-serif",
    layoutMode: 'sidebar',
    colors: {
      // Same palette as 'loop'
      primary: '#18317a',
      primaryDark: '#0d1a42',
      primaryLight: '#264cbd',
      primaryForeground: '#ffffff',

      success: '#10b981',
      warning: '#f59e0b',
      danger: '#f3463d',
      info: '#264cbd',

      surface: '#ffffff',
      surfaceAlt: '#f5f7fc',
      surfacePage: '#f8f9fd',

      border: '#dde1ed',
      borderSubtle: '#c3cade',

      text: '#272b38',
      textMuted: '#757f9a',
      textHeading: '#272b38',
    },
  },
};

// CSS variables mapping for each theme
export function getThemeCSSVariables(theme: ThemeConfig): Record<string, string> {
  return {
    '--color-primary': theme.colors.primary,
    '--color-primary-dark': theme.colors.primaryDark,
    '--color-primary-light': theme.colors.primaryLight,
    '--color-primary-foreground': theme.colors.primaryForeground,
    '--color-success': theme.colors.success,
    '--color-warning': theme.colors.warning,
    '--color-danger': theme.colors.danger,
    '--color-info': theme.colors.info,
    '--color-surface': theme.colors.surface,
    '--color-surface-alt': theme.colors.surfaceAlt,
    '--color-surface-page': theme.colors.surfacePage,
    '--color-border': theme.colors.border,
    '--color-border-subtle': theme.colors.borderSubtle,
    '--color-text': theme.colors.text,
    '--color-text-muted': theme.colors.textMuted,
    '--color-text-heading': theme.colors.textHeading,
    '--font-family': theme.fontFamily,
    '--font-family-heading': theme.headingFontFamily,
  };
}

// Default theme
export const DEFAULT_THEME: ThemeName = 'loop-full';

// localStorage key
export const THEME_STORAGE_KEY = 'gmach-finance-theme';
